import { IonChip, IonIcon, IonLabel } from '@ionic/react';
import { sparklesOutline } from 'ionicons/icons';
import { ReactElement } from 'react';
import { InformationButton } from '../../../../../../components/button/information-button';
import { IonItem } from '../../../../../../components/ionic/IonItem';
import { trackCobotWelderEnhancedStitchNudge } from '../../../../../../config/analytics';
import {
  IntercomArticle,
  useLaunchIntercomArticle,
} from '../../../../../../config/intercom';
import { i18nKeyType } from '../../../../../../i18n';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { Platform, getPlatform } from '../../../../../../util/platform';
import { TeachItemPickerOptions } from '../../../../shared/component/teach/option/teach-item-picker-options';
import { TeachMoveOptions } from '../../../../shared/component/teach/option/teach-move-options';
import { TriggerOutputOptions } from '../../../../shared/component/teach/option/trigger-output-options';
import { WaitForOptions } from '../../../../shared/component/teach/option/wait-for-options';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import {
  FlowItem,
  FlowItemParameterMetadata,
  FlowItemRef,
} from '../../../../shared/model/flow.types';
import { BeaconWeldingData } from '../../model/beacon-welding-data';
import './welding-teach.css';

export interface TeachOptionProps {
  optionId: string;
  icon?: string;
  description?: i18nKeyType | string;
  itemRef?: FlowItemRef;
  isSelected: boolean;
  isOtherSelected?: boolean;
  chipLabel?: string;
  isDisabled?: boolean;
  onAdd: (item?: FlowItem) => void;
  onSelect: () => void;
  onDone: () => void;
}

const WeldingTeachOption = (props: TeachOptionProps): ReactElement => {
  const {
    icon,
    description,
    itemRef,
    isSelected,
    isOtherSelected,
    chipLabel,
    onAdd,
    onDone,
    onSelect,
    isDisabled,
  } = props;

  const platform = getPlatform();
  const isWeb = platform === Platform.WEB || platform === Platform.MOBILE_WEB;

  const { flowItemService } = useFlowServices<BeaconWeldingData.FlowConfig>();

  const requiredParameters: FlowItemParameterMetadata[] = [];
  if (itemRef) {
    requiredParameters.push(
      ...flowItemService.getRequiredParametersForTeaching(itemRef)
    );
  }

  const classList: string[] = ['option-item transparent-background'];
  if (isOtherSelected) {
    classList.push('dim');
  }
  if (isSelected) {
    classList.push('option-selected');
  }

  return (
    <div>
      <IonItem
        data-cy={`teach-option-${props.optionId?.replace(/ /g, '-')}`}
        data-cy-selected={isSelected}
        className={classList.join(' ')}
        lines="none"
        detail={false}
        onClick={(e) => {
          e.preventDefault();
          if (isSelected) {
            onDone();
          } else {
            onSelect();
          }
        }}
        {...(!isWeb && { href: '#' })}
      >
        <IonIcon icon={icon} slot="start" />
        <IonLabel>{getLocalizedText(description as i18nKeyType)}</IonLabel>
        {isSelected && (
          <IonChip
            className="add-button"
            slot="end"
            onClick={(e) => {
              e.preventDefault();
              onAdd();
            }}
            disabled={isDisabled ?? false}
          >
            {chipLabel || getLocalizedText('welding:add')}
          </IonChip>
        )}
      </IonItem>
      {itemRef && (
        <FlowItemSubpanel itemRef={itemRef} show={isSelected} onAdd={onAdd} />
      )}
    </div>
  );
};

interface FlowItemSubpanelProps {
  itemRef: FlowItemRef;
  show: boolean;
  onAdd: () => void;
}

const FlowItemSubpanel = (
  props: FlowItemSubpanelProps
): ReactElement | null => {
  const { itemRef, onAdd, show } = props;

  const launchArticle = useLaunchIntercomArticle();

  if (show) {
    if (itemRef === BeaconWeldingData.FlowItemRef.WELD) {
      return (
        <div className="sub-panel-overlay teach-panel-type-options">
          <TeachItemPickerOptions
            onAdd={onAdd}
            parameterMetadata={BeaconWeldingData.WeldTypeMetadata}
          >
            <div className="ion-padding">
              <InformationButton
                icon={sparklesOutline}
                message={
                  getLocalizedText('welding:enhanced_stitch_feature_nudge') ??
                  'Discover a new way to create stitches!'
                }
                onClick={async () => {
                  trackCobotWelderEnhancedStitchNudge();
                  await launchArticle(IntercomArticle.ENHANCED_STITCH_WELD);
                }}
              />
            </div>
          </TeachItemPickerOptions>
        </div>
      );
    } else if (itemRef === BeaconWeldingData.FlowItemRef.MOVE) {
      return <TeachMoveOptions onAdd={onAdd} />;
    } else if (itemRef === BeaconWeldingData.FlowItemRef.WAIT_FOR) {
      return (
        <div className="sub-panel-overlay teach-panel-type-options">
          <WaitForOptions onAdd={onAdd} />
        </div>
      );
    } else if (itemRef === BeaconWeldingData.FlowItemRef.TRIGGER_OUTPUT) {
      return (
        <div className="sub-panel-overlay teach-panel-type-options">
          <TriggerOutputOptions onAdd={onAdd} />
        </div>
      );
    }
  }
  return null;
};

export { WeldingTeachOption };
